import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, Smartphone, Globe } from "lucide-react";
import { NAV } from "@/config/nav";
import { useI18n } from "@/i18n/I18nProvider";
import { useCta } from "@/context/SettingsContext";
import { GpuLogo } from "@/components/brand/GpuLogo";
import { Btn } from "@/components/ui-kit/Kit";

const LangSwitch = ({ compact = false }) => {
  const { lang, setLang, languages } = useI18n();
  return (
    <div
      className="flex items-center gap-1 rounded-full border border-navy/15 p-1"
      data-testid="language-switcher"
    >
      <Globe className="ml-2 h-3.5 w-3.5 text-navy/50" aria-hidden="true" />
      {languages.map((l) => (
        <button
          key={l.code}
          type="button"
          onClick={() => setLang(l.code)}
          data-testid={`lang-btn-${l.code}`}
          aria-pressed={lang === l.code}
          className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors duration-200 ${
            lang === l.code ? "bg-navy text-white" : "text-navy/60 hover:text-navy"
          } ${compact ? "" : ""}`}
        >
          {l.short}
        </button>
      ))}
    </div>
  );
};

const Dropdown = ({ item, t }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`nav-${item.key.split(".")[1]}`}
        className="flex items-center gap-1 whitespace-nowrap py-2 font-display text-[12px] font-medium uppercase tracking-[0.08em] text-navy/75 transition-colors duration-200 hover:text-navy"
      >
        {t(item.key)}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.22 }}
            className="absolute left-0 top-full z-50 w-[290px] border border-navy/10 bg-white/95 p-2 shadow-[0_24px_60px_-24px_rgba(10,25,47,0.35)] backdrop-blur-xl"
          >
            {item.children.map((c) => (
              <NavLink
                key={c.to}
                to={c.to}
                data-testid={`nav-link-${c.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `block border-l-2 px-4 py-2.5 text-[13px] transition-colors duration-200 ${
                    isActive
                      ? "border-grn bg-grn-pale text-navy"
                      : "border-transparent text-slate-600 hover:border-grn hover:bg-slate-50 hover:text-navy"
                  }`
                }
              >
                {t(c.key)}
              </NavLink>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Header = () => {
  const { t } = useI18n();
  const cta = useCta();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobile(false);
    setOpenGroup(null);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobile ? "hidden" : "";
  }, [mobile]);

  return (
    <>
      <div className="hidden bg-navy-deep py-2 text-white lg:block">
        <div className="container flex items-center justify-between">
          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-navy-300">
            {t("brand.positioning")}
          </p>
          <div className="flex items-center gap-5">
            <a
              href={cta.member_login_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="topbar-member-login"
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/70 transition-colors duration-200 hover:text-gold"
            >
              {t("cta.memberLogin")}
            </a>
            <span className="h-3 w-px bg-white/20" />
            <a
              href={cta.vendor_login_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="topbar-vendor-login"
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/70 transition-colors duration-200 hover:text-gold"
            >
              {t("cta.vendorLogin")}
            </a>
            <span className="h-3 w-px bg-white/20" />
            <a
              href={cta.android_app_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="topbar-app-link"
              className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold"
            >
              <Smartphone className="h-3 w-3" /> {t("cta.getApp")}
            </a>
          </div>
        </div>
      </div>

      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-300 ${
          scrolled
            ? "border-navy/10 bg-white/85 shadow-[0_10px_40px_-30px_rgba(10,25,47,0.6)] backdrop-blur-xl"
            : "border-transparent bg-white"
        }`}
      >
        <div className="container flex items-center justify-between gap-3 py-3 sm:gap-6 sm:py-3.5">
          <Link to="/" data-testid="logo-link" aria-label="Gujarat Pharma Union home">
            <GpuLogo compact />
          </Link>

          <nav className="hidden items-center gap-5 xl:flex" aria-label="Primary">
            {NAV.map((item) =>
              item.children ? (
                <Dropdown key={item.key} item={item} t={t} />
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-link-${item.to.replace(/\//g, "") || "home"}`}
                  className={({ isActive }) =>
                    `whitespace-nowrap py-2 font-display text-[12px] font-medium uppercase tracking-[0.08em] transition-colors duration-200 ${
                      isActive ? "text-grn" : "text-navy/75 hover:text-navy"
                    }`
                  }
                >
                  {t(item.key)}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <div className="hidden lg:block">
              <LangSwitch />
            </div>
            <Btn
              href={cta.member_registration_url || "#"}
              external
              size="sm"
              variant="solid"
              className="hidden md:inline-flex"
              data-testid="header-join-member"
            >
              {t("cta.joinMember")}
            </Btn>
            <button
              type="button"
              onClick={() => setMobile(true)}
              data-testid="mobile-menu-open"
              aria-label={t("nav.menu")}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-navy/15 text-navy xl:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex flex-col bg-navy text-white"
            data-testid="mobile-menu"
          >
            <div className="flex shrink-0 items-center justify-between border-b border-white/10 px-5 py-3.5 sm:px-6">
              <GpuLogo surface="dark" compact />
              <button
                type="button"
                onClick={() => setMobile(false)}
                data-testid="mobile-menu-close"
                aria-label={t("nav.close")}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/20"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-16 pt-6 sm:px-6">
              {NAV.map((item) =>
                item.children ? (
                  <div key={item.key} className="border-b border-white/10">
                    <button
                      type="button"
                      onClick={() => setOpenGroup(openGroup === item.key ? null : item.key)}
                      data-testid={`mobile-group-${item.key.split(".")[1]}`}
                      className="flex w-full items-center justify-between py-4 font-display text-base uppercase tracking-[0.08em]"
                    >
                      {item.children ? t(item.key) : t(item.key)}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-300 ${
                          openGroup === item.key ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    <AnimatePresence>
                      {openGroup === item.key && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="pb-4">
                            {item.children.map((c) => (
                              <Link
                                key={c.to}
                                to={c.to}
                                data-testid={`mobile-link-${c.to.replace(/\//g, "")}`}
                                className="block border-l border-white/15 py-2.5 pl-4 text-sm text-white/70"
                              >
                                {t(c.key)}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={`mobile-link-${item.to.replace(/\//g, "") || "home"}`}
                    className="block border-b border-white/10 py-4 font-display text-base uppercase tracking-[0.08em]"
                  >
                    {t(item.key)}
                  </Link>
                )
              )}
              <div className="mt-8 flex flex-col gap-3">
                <Btn href={cta.member_registration_url || "#"} external variant="solid" data-testid="mobile-join-member">
                  {t("cta.joinMember")}
                </Btn>
                <Btn href={cta.vendor_registration_url || "#"} external variant="ghostLight" data-testid="mobile-register-vendor">
                  {t("cta.registerVendor")}
                </Btn>
                <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-white/15 p-4">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
                    {t("common.language")}
                  </span>
                  <div className="rounded-full bg-white p-0.5">
                    <LangSwitch compact />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
